import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";
import RegisterFormView from "@/pages/register/components/views/register-form-view";
import { Languages } from "lucide-react";
import { routes } from "@/consts";
import { cn } from "@/lib/utils";
import { FaHospitalUser, FaUserTie } from "react-icons/fa";
import { Radio } from "@mui/material";
import { useState } from "react";
import { Toaster } from "@/app/global-components/toaster";

enum ROLE {
  BUYER = "buyer",
  SELLER = "seller",
}
const Register = ({ role }: { role: ROLE }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const getNextLocale = () => {
    return locale === LANG.EN ? LANG.AR : LANG.EN;
  };
  const handleLanguageSwap = () => {
    const pathName = location.pathname;
    if (!pathName) return routes.home;
    const segments = pathName.split("/");
    segments[1] = getNextLocale();
    return segments.join("/");
  };
  const navigate = useNavigate();
  return (
    <>
      <h1 className="text-2xl font-semibold  sm:text-3xl">
        {t("authentication.register")}
      </h1>
      <section className="w-full mt-5 max-w-xs bg-primary-white p-5 shadow-xl rounded-md sm:max-w-lg">
        <div className="flex flex-col"></div>
        <RegisterFormView Roll={role == ROLE.BUYER ? "Buyer" : "Seller"} />
        <Toaster />
        <div className="flex flex-row justify-between items-start mt-4">
          <div
            onClick={() => navigate(handleLanguageSwap())}
            className="cursor-pointer text-lg flex items-center  gap-2"
          >
            <Languages />
            <div>{locale == LANG.AR ? "عربي" : "english"}</div>
          </div>
          <p className="text-textcolor-mediumGray text-sm sm:text-base">
            {t("authentication.messages.haveAccount")}{" "}
            <Link
              to={routes.login}
              state={{ role: ROLE.BUYER }}
              className="font-bold text-purple-700 underline-offset-4"
            >
              {t("authentication.login")}
            </Link>
          </p>
        </div>
      </section>
    </>
  );
};
export default function RegisterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const setRole = () => {
    setSearchParams({ role: selectedValue });
  };
  const role = searchParams.get("role");
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [selectedValue, setSelectedValue] = useState<ROLE>(
    (role as ROLE) || ROLE.BUYER
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value as ROLE);
  };

  return (
    <div>
      <div className="flex items-center justify-between p-5">
        <div
          className={cn("text-3xl font-bold", {
            "flex-row-reverse": locale == LANG.AR,
          })}
        >
          M<span>edex</span>
        </div>
      </div>
      <main className="flex min-h-[calc(100vh-50px)] flex-col items-center justify-center px-4 sm:px-6">
        {role ? (
          <Register role={role as ROLE} />
        ) : (
          <>
            <div className="text-3xl font-bold">
              {t("authentication.registInfo.join")}
            </div>
            <div className="mt-6">
              <div className="flex flex-wrap md:flex-nowrap justify-center gap-5">
                <div
                  className={cn(
                    "flex gap-5 w-full justify-between border p-5 cursor-pointer rounded-lg",
                    selectedValue == ROLE.BUYER && "border-2 border-purple-600"
                  )}
                  onClick={() => setSelectedValue(ROLE.BUYER)}
                >
                  <div className="flex flex-col gap-5">
                    <div>
                      <FaHospitalUser className="text-2xl" />
                    </div>
                    <div className="text-xl min-w-64 font-medium">
                      {t("authentication.registInfo.applyRegister")}
                    </div>
                  </div>
                  <div>
                    <Radio
                      checked={selectedValue === ROLE.BUYER}
                      onChange={handleChange}
                      value={ROLE.BUYER}
                      color="secondary"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "A" }}
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "flex gap-5 border w-full justify-between p-5 cursor-pointer rounded-lg items-center",
                    selectedValue == ROLE.SELLER && "border-2 border-purple-600"
                  )}
                  onClick={() => setSelectedValue(ROLE.SELLER)}
                >
                  <div className="flex flex-col gap-5">
                    <div>
                      <FaUserTie className="text-2xl" />
                    </div>
                    <div className="text-xl max-w-56 font-medium">
                      {t("authentication.registInfo.applySellerRegister")}
                    </div>
                  </div>
                  <div>
                    <Radio
                      checked={selectedValue === ROLE.SELLER}
                      onChange={handleChange}
                      value={ROLE.SELLER}
                      color="secondary"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "B" }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 w-fit mx-auto">
                <div
                  onClick={setRole}
                  className="px-6 py-3 bg-black text-center hover:opacity-90 text-white rounded-full font-bold mx-auto lg:mr-0 lg:ml-auto mb-8 lg:mb-0 cursor-pointer"
                >
                  {selectedValue == ROLE.BUYER
                    ? t("authentication.registInfo.buyerBtn")
                    : t("authentication.registInfo.sellerBtn")}
                </div>
                <p className="text-textcolor-mediumGray mt-4 text-sm sm:text-base">
                  {t("authentication.messages.haveAccount")}{" "}
                  <Link
                    to={routes.login}
                    state={{ role: ROLE.BUYER }}
                    className="font-bold text-purple-700 underline-offset-4"
                  >
                    {t("authentication.login")}
                  </Link>
                </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
